import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const EssencePerfume = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  // All segment attributes filtered by visibleFields
  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-essence-perfume">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Decorative: radial glow ── */}
      <div style={{
        position: "absolute", top: "20%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 320, height: 320, borderRadius: "50%",
        background: "radial-gradient(circle,var(--theme-accent) 0%,transparent 70%)",
        opacity: 0.1, zIndex: 1, pointerEvents: "none",
      }} />
      {/* ── Decorative: concentric rings ── */}
      {[200, 280, 360].map((size, i) => (
        <div key={i} style={{
          position: "absolute", top: "28%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: size, height: size, borderRadius: "50%",
          border: "1px solid var(--theme-accent)",
          opacity: 0.08 + i * 0.04, zIndex: 1, pointerEvents: "none",
        }} />
      ))}
      {/* ── Decorative: dot columns ── */}
      {["left:10px", "right:10px"].map((side, si) => (
        <div key={si} style={{
          position: "absolute", top: 60, bottom: 60,
          [side.split(":")[0]]: side.split(":")[1],
          display: "flex", flexDirection: "column",
          justifyContent: "space-around", alignItems: "center",
          zIndex: 2, pointerEvents: "none", opacity: 0.35,
        }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{
              width: 3, height: 3, borderRadius: "50%",
              background: "var(--theme-accent)",
            }} />
          ))}
        </div>
      ))}
      {/* ── Accent top bar ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 4,
        background: "var(--theme-accent)", zIndex: 10,
      }} />

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column",
        height: "100%", padding: "8px 22px 14px",
      }}>

        {/* ── Brand bar ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          paddingTop: 10, marginBottom: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 3 }} />
            )}
            {isVisible("organizationName") && (
              <span style={{
                fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
                fontSize: "0.72rem", fontWeight: 800,
                color: "var(--theme-text)",
                textTransform: "uppercase", letterSpacing: "0.15em",
              }}>
                {(buyer?.organizationName || "YOUR BRAND").toUpperCase()}
              </span>
            )}
          </div>
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.55rem", fontWeight: 700,
            padding: "3px 8px", borderRadius: 20, letterSpacing: "0.1em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            {product?.segment?.toUpperCase() || "JEWELLERY"}
          </div>
        </div>

        {/* ── Headline ── */}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Playfair Display',serif)",
            fontSize: "1.6rem", fontStyle: "italic", fontWeight: 400,
            color: "var(--theme-textMuted)", lineHeight: 1, marginBottom: 2,
          }}>
            Unleash
          </div>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "1.1rem", fontWeight: 900,
            color: "var(--theme-text)",
            letterSpacing: "0.2em", textTransform: "uppercase", lineHeight: 1.2,
          }}>
            YOUR ESSENCE
          </div>
          <div style={{
            width: 48, height: 2, background: "var(--theme-accent)",
            margin: "5px auto 0", borderRadius: 2,
          }} />
        </div>

        {/* ── Product image ── */}
        <div style={{
          position: "relative", flex: "0 0 auto",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 10px", height: 160,
        }}>
          <div style={{
            position: "absolute", width: 180, height: 180, borderRadius: "50%",
            background: "radial-gradient(circle,var(--theme-accent) 0%,transparent 70%)",
            opacity: 0.15,
          }} />
          {[
            { top: 8, left: "20%" }, { top: 8, right: "20%" },
            { bottom: 8, left: "25%" }, { bottom: 8, right: "25%" },
          ].map((pos, i) => (
            <div key={i} style={{
              position: "absolute", ...pos,
              width: 7, height: 7,
              background: "var(--theme-accent)", opacity: 0.5,
              transform: "rotate(45deg)",
            }} />
          ))}
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "75%", maxHeight: "100%", objectFit: "contain",
              position: "relative", zIndex: 2,
              filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.4))",
            }}
          />
        </div>

        {/* ── Product name ── */}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <h2 style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "0.95rem", fontWeight: 700,
            color: "var(--theme-text)", margin: "0 0 4px",
          }}>
            {product?.name || "Fine Jewellery"}
          </h2>
        </div>

        {/* ── All segment attributes ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 8px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)",
            borderRadius: 4, padding: "6px 10px", marginBottom: 8,
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.5rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                    <DiamondShapeSVG
                      shapeName={String(v)}
                      size={26}
                      color="var(--theme-accent)"
                    />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600,
                      color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>
                      {fmtVal(v)}
                    </span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.66rem", fontWeight: 600,
                    color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>
                    {fmtVal(v)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Tagline ── */}
        <p style={{
          textAlign: "center", fontSize: "0.64rem", lineHeight: 1.4,
          color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          margin: "0 0 8px", fontStyle: "italic",
        }}>
          {product?.description
            ? product.description.slice(0, 75) + (product.description.length > 75 ? "…" : "")
            : `Find your signature ${product?.segment || "jewellery"} piece.`}
        </p>

        {/* ── Price + CTA ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "var(--theme-surface)",
          border: "1px solid var(--theme-border)",
          borderTop: "2px solid var(--theme-accent)",
          borderRadius: 4, padding: "7px 12px", marginBottom: 8,
        }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "1.05rem", fontWeight: 800, color: "var(--theme-accent)",
          }}>
            {formatPrice(price)}
          </div>
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.62rem", fontWeight: 800,
            padding: "6px 14px", borderRadius: 20, letterSpacing: "0.1em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            › AVAILABLE NOW
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          gap: 8, fontSize: "0.52rem", fontWeight: 600, letterSpacing: "0.12em",
          color: "var(--theme-textMuted)",
          borderTop: "1px solid var(--theme-border)", paddingTop: 6,
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          flexWrap: "wrap",
        }}>
          {isVisible("address") && (
            <span>{buyer?.streetAddress || "123 Jewellers Lane"}</span>
          )}
          {isVisible("address") && isVisible("phone") && (
            <span style={{ color: "var(--theme-accent)", opacity: 0.6 }}>|</span>
          )}
          {isVisible("phone") && (
            <span>{buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>
          )}
          {(isVisible("address") || isVisible("phone")) && isVisible("organizationName") && (
            <span style={{ color: "var(--theme-accent)", opacity: 0.6 }}>|</span>
          )}
          {isVisible("organizationName") && (
            <span style={{ color: "var(--theme-accent)" }}>
              {(buyer?.organizationName || "JMI").toUpperCase()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

EssencePerfume.displayName = "EssencePerfume";
export default EssencePerfume;
